import { Injectable } from '@angular/core';
import { getRandomId } from '@asamco/utils';
import { filter } from 'rxjs/operators';
import { TopNavBarButton, TopNavOptions } from '../top-bar/top-bar-button.interface';
import { TopBarService } from '../top-bar/top-bar.service';

import { IFrameChildService } from './i-frame-child.service';
import { MessageType } from './message-type.enum';

@Injectable({
	providedIn: 'root',
})
export class IFrameTopbarService extends TopBarService {
	private readonly map = new Map();

	constructor(private childService: IFrameChildService) {
		super();
		// bind all methods to this class,
		this.addButton = this.addButton.bind(this);
		this.updateButton = this.updateButton.bind(this);
		this.addButtons = this.addButtons.bind(this);
		this.clearAllButtons = this.clearAllButtons.bind(this);
		this.replaceButtons = this.replaceButtons.bind(this);
		this.removeButtonById = this.removeButtonById.bind(this);
		this.init();
	}

	init() {
		this.childService.incomingMessages$.pipe(filter(m => m.type === 'topNav')).subscribe((m: any) => {
			if (m.payload.action === 'click') {
				const id = m.payload.id;
				if (this.map.has(id)) {
					const action = this.map.get(id);
					if (action) {
						action();
					} else {
						console.error('No action was attached to this button!');
					}
				}
			}
			if (m.payload.action === 'clearAllButtons') {
				// clear out existing handlers, to prevent leakage
				this.map.clear();
			}
		});
	}

	addButton(options) {
		if (options.onClick === undefined && (!options.children || options.children.length === 0)) {
			throw new Error(`trying to add a topNavButton without a click handler`);
		}

		const children = [];
		if (options.children) {
			for (const i of options.children.keys()) {
				if (options.children[i].onClick === undefined) {
					throw new Error(`trying to add a child topNavButton without a click handler`);
				}
				children[i] = Object.assign({}, options.children[i], {
					id: options.children[i].id ? options.children[i].id : getRandomId(),
					onClick: undefined,
				});
			}
		}

		const payload = {
			action: 'add',
			button: Object.assign({}, options, { id: options.id ? options.id : getRandomId(), onClick: undefined, children: children }),
		};
		this.map.set(payload.button.id, options.onClick);

		for (const i of children.keys()) {
			this.map.set(children[i].id, options.children[i].onClick);
		}

		this.childService.send({ type: MessageType.topNav, payload });
	}

	updateButton(options: TopNavOptions) {
		if (options.onClick === undefined) {
			throw new Error(`trying to replace a topNavButton without a click handler`);
		}
		const payload = {
			action: 'update',
			button: Object.assign({}, options, { id: options.id ? options.id : getRandomId(), onClick: undefined }),
		};
		this.map.set(payload.button.id, options.onClick);
		this.childService.send({ type: MessageType.topNav, payload });
	}

	addButtons(optionsArray) {
		optionsArray.forEach(this.addButton);
	}

	removeButtonById(id: string): void {
		this.childService.send({ type: MessageType.topNav, payload: { action: 'remove', id: id } });
		this.map.delete(id);
	}

	spin(id, isSpinning) {
		this.childService.send({ type: MessageType.topNav, payload: <any>{ action: 'spin', id: id, isSpinning: isSpinning } });
	}

	replaceButtons(buttons: TopNavBarButton[], clearInnerAppBtns = true): void {
		this.clearAllButtons(clearInnerAppBtns);
		this.addButtons(buttons);
	}

	clearAllButtons(clearInnerAppBtns = true) {
		this.map.clear();
		this.childService.send({ type: MessageType.topNav, payload: <any>{ action: 'clearAllButtons' } });
	}
}
